/**
 * User-scoped connector configuration stored in localStorage.
 * Supports multiple named profiles; the active profile's values are
 * returned by getConnectorConfig so all pages are profile-aware automatically.
 *
 * localStorage is a per-browser cache, not per-account storage - it never
 * followed the user across devices/browsers on its own. Profiles are now
 * also mirrored to the backend (/api/connectors, scoped to the logged-in
 * user's JWT identity) as the real source of truth: syncProfilesFromServer()
 * hydrates localStorage from there on login, and saveProfile/deleteProfile
 * push changes back in the background. All existing synchronous exports
 * keep their original signatures so no caller needs to change.
 */

import axios from 'axios';

// Same palette Header.jsx's own COLORS array uses. Kept as a deterministic
// hash of the profile id (not a random/incrementing pick) so a connector
// synced down for the first time gets the same color in every browser,
// instead of always defaulting to blue here while a differently-colored
// local copy already existed elsewhere.
const COLOR_CLASSES = ['bg-blue-600', 'bg-emerald-600', 'bg-purple-600', 'bg-orange-500', 'bg-pink-600', 'bg-teal-600', 'bg-red-600', 'bg-indigo-500'];
function _colorForId(id) {
  let hash = 0;
  const s = String(id);
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return COLOR_CLASSES[hash % COLOR_CLASSES.length];
}

// Prefer decoding the identity straight out of the active JWT rather than
// trusting the separately-tracked 'username' localStorage key, which can
// drift out of sync with the real session - e.g. api.js's silent
// token-refresh path updates kc_token/kc_refresh_token but never touches
// 'username', so a refreshed-but-not-freshly-logged-in session could have
// a perfectly valid token while this returned null, silently skipping
// every profile save/sync in this file (return-early guards below).
function _getUsername() {
  const token = localStorage.getItem('kc_token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const derived = payload.preferred_username || payload.email || payload.sub;
      if (derived) return derived;
    } catch {
      // fall through to the legacy key
    }
  }
  return localStorage.getItem('username');
}

// The /api/connectors endpoints are registered directly on the Express app
// in app.js, not under the /api/v1 prefix the rest of the API (and
// services/api.js's apiClient, whose baseURL is /api/v1) uses. Header.jsx
// and ConsumerDashboard.jsx already call it as a plain "/api/connectors"
// axios request with a manually-attached token for the same reason -
// matching that convention here rather than going through apiClient, which
// would silently resolve to the wrong, non-existent /api/v1/connectors.
function _connectorsApi() {
  const token = localStorage.getItem('kc_token');
  return axios.create({
    baseURL: '/api/connectors',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

// ── Legacy single-config (fallback when no profile is active) ────────────────

function _getConfig() {
  const username = _getUsername();
  if (!username) return {};
  return JSON.parse(localStorage.getItem('connectorConfig_' + username) || '{}');
}

function _saveConfig(config) {
  const username = _getUsername();
  if (!username) return;
  localStorage.setItem('connectorConfig_' + username, JSON.stringify(config));
}

// ── Backend sync (best-effort, never blocks the synchronous localStorage API) ─

// One frontend profile can carry both a provider and a consumer connector;
// the backend stores one row per connector_type, so a profile maps to up to
// two rows sharing a "<profileId>::provider" / "<profileId>::consumer" id.
function _backendRowsForProfile(profile) {
  const rows = [];
  if (profile.providerEdcUrl && profile.providerDspUrl) {
    rows.push({
      connector_id: `${profile.id}::provider`,
      connector_name: profile.name,
      connector_type: 'provider',
      management_url: profile.providerEdcUrl,
      dsp_url: profile.providerDspUrl,
      api_key: profile.providerApiKey || profile.edc_api_key || '',
      color: profile.color,
    });
  }
  if (profile.consumerEdcUrl) {
    rows.push({
      connector_id: `${profile.id}::consumer`,
      connector_name: profile.name,
      connector_type: 'consumer',
      management_url: profile.consumerEdcUrl,
      color: profile.color,
      dsp_url: profile.consumerDspUrl || '',
      api_key: profile.consumerApiKey || profile.edc_api_key || '',
    });
  }
  return rows;
}

function _pushProfileToBackend(profile) {
  const rows = _backendRowsForProfile(profile);
  if (rows.length === 0) {
    console.warn('[connectorConfig] no rows to push for profile - missing a management URL + DSP URL pair for either type', profile.id);
    return;
  }
  const api = _connectorsApi();
  rows.forEach((row) => {
    api.post('/register', row)
      .catch((err) => console.error('[connectorConfig] register failed:', row.connector_id, err.response?.status, err.response?.data || err.message));
  });
}

function _pushProfileDeleteToBackend(id) {
  const api = _connectorsApi();
  api.delete(`/${id}::provider`).catch(() => { /* best-effort */ });
  api.delete(`/${id}::consumer`).catch(() => { /* best-effort */ });
}

// Pull this user's saved connectors from the backend and merge them into
// localStorage. Call this once after login (or whenever you want to make
// sure this browser reflects the account's real saved state), before
// reading profiles for the first time.
export async function syncProfilesFromServer() {
  const username = _getUsername();
  if (!username) return;

  try {
    const { data } = await _connectorsApi().get('/');
    const rows = data?.connectors || [];

    const byProfileId = new Map();
    for (const row of rows) {
      const [profileId, type] = String(row.connector_id).split('::');
      if (!profileId || !type) continue;
      const existing = byProfileId.get(profileId) || { id: profileId, name: row.connector_name };
      if (row.metadata?.color) existing.color = row.metadata.color;
      if (type === 'provider') {
        existing.providerEdcUrl = row.management_url;
        existing.provider_url = row.management_url;
        existing.assetsProviderUrl = row.management_url;
        existing.policiesProviderUrl = row.management_url;
        existing.providerDspUrl = row.dsp_url;
        existing.providerApiKey = row.api_key;
        existing.edc_api_key = row.api_key;
        existing.assetsApiKey = row.api_key;
        existing.policiesApiKey = row.api_key;
      } else if (type === 'consumer') {
        existing.consumerEdcUrl = row.management_url;
        existing.consumer_url = row.management_url;
        existing.consumerDspUrl = row.dsp_url;
        existing.consumerApiKey = row.api_key;
      }
      byProfileId.set(profileId, existing);
    }

    const localProfiles = getProfiles();
    const merged = new Map(localProfiles.map((p) => [p.id, p]));
    for (const [id, serverProfile] of byProfileId) {
      // Server is the source of truth for everything, including color now
      // that it's persisted in metadata - fall back to whatever's cached
      // locally, then to a deterministic per-id color, only for connectors
      // that predate color being synced at all.
      const local = merged.get(id) || {};
      merged.set(id, { ...local, ...serverProfile, color: serverProfile.color || local.color || _colorForId(id) });
    }

    if (merged.size > 0) {
      localStorage.setItem('connectorProfiles_' + username, JSON.stringify(Array.from(merged.values())));

      // "Which profile is active" now syncs too (see setActiveProfileId) -
      // the server's value wins when present, so every device shows the
      // same selection. Only fall back to auto-picking the first profile
      // when the account has never set one anywhere.
      let serverActiveId = null;
      try {
        const { data } = await _connectorsApi().get('/active');
        serverActiveId = data?.active_connector_id || null;
      } catch {
        // best-effort - fall through to local/default below
      }

      if (serverActiveId && merged.has(serverActiveId)) {
        localStorage.setItem('activeConnectorId_' + username, serverActiveId);
      } else if (!getActiveProfileId()) {
        setActiveProfileId(Array.from(merged.keys())[0]);
      }
    }

    // One-way migration for profiles that predate this sync feature: anything
    // saved locally before the backend had anywhere to write to (or before
    // this browser ever ran this code) never got pushed up. Push it now so
    // it becomes visible on every other device too.
    for (const profile of localProfiles) {
      if (!byProfileId.has(profile.id)) {
        _pushProfileToBackend(profile);
      }
    }
  } catch (err) {
    console.error('[connectorConfig] syncProfilesFromServer FAILED:', err.response?.status, err.response?.data || err.message);
  }
}

// ── Profile management ────────────────────────────────────────────────────────

export function getProfiles() {
  const username = _getUsername();
  if (!username) return [];
  return JSON.parse(localStorage.getItem('connectorProfiles_' + username) || '[]');
}

export function saveProfile(profile) {
  const username = _getUsername();
  if (!username) return;
  const profiles = getProfiles();
  const idx = profiles.findIndex(p => p.id === profile.id);
  if (idx >= 0) profiles[idx] = profile;
  else profiles.push(profile);
  localStorage.setItem('connectorProfiles_' + username, JSON.stringify(profiles));
  _pushProfileToBackend(profile);
}

export function deleteProfile(id) {
  const username = _getUsername();
  if (!username) return;
  const profiles = getProfiles().filter(p => p.id !== id);
  localStorage.setItem('connectorProfiles_' + username, JSON.stringify(profiles));
  _pushProfileDeleteToBackend(id);
}

export function getActiveProfileId() {
  const username = _getUsername();
  if (!username) return null;
  return localStorage.getItem('activeConnectorId_' + username) || null;
}

export function setActiveProfileId(id) {
  const username = _getUsername();
  if (!username) return;
  if (id) localStorage.setItem('activeConnectorId_' + username, id);
  else localStorage.removeItem('activeConnectorId_' + username);
  // Was purely local before - the same account could show a different
  // connector "active" on different devices, which is exactly the kind of
  // inconsistency the rest of this sync feature was built to avoid.
  _connectorsApi().put('/active', { connector_id: id || null })
    .catch((err) => console.error('[connectorConfig] failed to sync active connector:', err.response?.status, err.message));
}

export function getActiveProfile() {
  const id = getActiveProfileId();
  if (!id) return null;
  return getProfiles().find(p => p.id === id) || null;
}

// ── Config read/write (profile-aware) ────────────────────────────────────────

export function getConnectorConfig(key) {
  const profile = getActiveProfile();
  if (profile && profile[key] !== undefined) return profile[key] || '';
  return _getConfig()[key] || '';
}

export function saveConnectorConfig(key, value) {
  const activeId = getActiveProfileId();
  if (activeId) {
    const profiles = getProfiles();
    const idx = profiles.findIndex(p => p.id === activeId);
    if (idx >= 0) {
      profiles[idx][key] = value;
      const username = _getUsername();
      if (username) localStorage.setItem('connectorProfiles_' + username, JSON.stringify(profiles));
      return;
    }
  }
  const config = _getConfig();
  config[key] = value;
  _saveConfig(config);
}

export function saveConnectorConfigs(entries) {
  const activeId = getActiveProfileId();
  if (activeId) {
    const profiles = getProfiles();
    const idx = profiles.findIndex(p => p.id === activeId);
    if (idx >= 0) {
      Object.entries(entries).forEach(([k, v]) => { profiles[idx][k] = v; });
      const username = _getUsername();
      if (username) localStorage.setItem('connectorProfiles_' + username, JSON.stringify(profiles));
      return;
    }
  }
  const config = _getConfig();
  Object.entries(entries).forEach(([key, value]) => { config[key] = value; });
  _saveConfig(config);
}
